// Form and selection controls (mockup ui.seg / ui.tabs / ui.chips / ui.switch / ui.select / ui.input / ui.search /
// ui.pager). Two selection looks only (plan 8.1.1): first-level choice = black pill (Tabs, Chips),
// second-level switch = white pill in a grey track (Seg). Keyboard: ←→ (Home/End) inside groups, roving tabindex.
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useLang, useT } from '../i18n';
import { reduceMotion } from '../lib/media';
import { cls, debounce } from '../lib/util';
import { Icon, type IconName } from './Icon';

/** Arrow keys move between items of a group (and select them when `select`). */
function rovingKeyDown(e: ReactKeyboardEvent<HTMLElement>, itemSel: string, opts?: { vertical?: boolean; select?: boolean }) {
  const keys = opts?.vertical ? ['ArrowDown', 'ArrowUp'] : ['ArrowRight', 'ArrowLeft'];
  if (![keys[0], keys[1], 'Home', 'End'].includes(e.key)) return;
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>(itemSel)).filter((x) => !x.disabled);
  const i = items.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) return;
  e.preventDefault();
  const n = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (i + (e.key === keys[0] ? 1 : -1) + items.length) % items.length;
  items[n]?.focus();
  if (opts?.select) items[n]?.click();
}

// ───────────────────────── segmented (second-level switch, radiogroup) ─────────────────────────
export interface SegItem<V extends string> {
  value: V;
  label: ReactNode;
  icon?: IconName;
  disabled?: boolean;
  title?: string;
  /** Extra content inside the option (period [직접] range summary). */
  extra?: ReactNode;
  /** Extra props for that option button (aria-haspopup …). */
  onClickCapture?: () => void;
}
export interface SegProps<V extends string> {
  items: ReadonlyArray<SegItem<V>>;
  value: V;
  onChange?: (v: V) => void;
  label?: string;
  size?: 'sm';
  className?: string;
  /** Call onChange even when the value does not change (period [직접] reopens its picker). */
  always?: boolean;
}
export function Seg<V extends string>({ items, value, onChange, label, size, className, always }: SegProps<V>) {
  const hasChecked = items.some((it) => it.value === value);
  return (
    <div className={cls('seg', size && `seg--${size}`, className)} role="radiogroup" aria-label={label || undefined} onKeyDown={(e) => rovingKeyDown(e, '.seg__opt', { select: true })}>
      {items.map((it, i) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            className="seg__opt"
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on || (!hasChecked && i === 0) ? 0 : -1}
            disabled={it.disabled}
            title={it.title}
            data-value={it.value}
            onClick={() => {
              it.onClickCapture?.();
              if (it.value !== value || always) onChange?.(it.value);
            }}
          >
            {it.icon ? <Icon name={it.icon} /> : null}
            {it.label}
            {it.extra ?? null}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────── tabs (first-level choice, tablist) ─────────────────────────
export interface TabItem<V extends string> {
  value: V;
  label: ReactNode;
  count?: number;
  icon?: IconName;
  disabled?: boolean;
}
export interface TabsProps<V extends string> {
  items: ReadonlyArray<TabItem<V>>;
  value: V;
  onChange?: (v: V) => void;
  label?: string;
  /** pill = black sliding pill (default) · underline = line tabs */
  variant?: 'pill' | 'underline';
  size?: 'sm';
  /** ids: <idPrefix>-tab-<value>, panels <idPrefix>-panel-<value> (aria-controls) */
  idPrefix?: string;
  className?: string;
}
const PILL_MS = 400;
const PILL_EASE = 'cubic-bezier(.2,.8,.2,1)';
interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
const boxOf = (el: HTMLElement): Box => ({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight });
const xf = (b: Box) => `translate(${b.x}px,${b.y}px)`;
function placeInd(ind: HTMLElement, b: Box) {
  ind.style.width = `${b.w}px`;
  ind.style.height = `${b.h}px`;
  ind.style.transform = xf(b);
}

export function Tabs<V extends string>({ items, value, onChange, label, variant = 'pill', size, idPrefix, className }: TabsProps<V>) {
  const { format } = useTabNumber();
  const ref = useRef<HTMLDivElement>(null);
  const indRef = useRef<HTMLSpanElement>(null);
  const prevBox = useRef<Box | null>(null);
  const pill = variant === 'pill';
  const [hasInd, setHasInd] = useState(false);
  const [more, setMore] = useState({ l: false, r: false });

  // sliding pill: from the old tab's box to the new one (0.4 s); reduced motion jumps
  useLayoutEffect(() => {
    const el = ref.current;
    const ind = indRef.current;
    if (!pill || !el || !ind) return;
    const sel = el.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!sel) return;
    const to = boxOf(sel);
    const from = prevBox.current;
    placeInd(ind, to);
    setHasInd(true);
    if (from && !reduceMotion() && typeof ind.animate === 'function' && (from.x !== to.x || from.y !== to.y || from.w !== to.w)) {
      ind.animate(
        [
          { width: `${from.w}px`, height: `${from.h}px`, transform: xf(from) },
          { width: `${to.w}px`, height: `${to.h}px`, transform: xf(to) },
        ],
        { duration: PILL_MS, easing: PILL_EASE },
      );
    }
    prevBox.current = to;
  }, [value, pill, items.length]);

  // keep the pill on its tab when fonts load or the bar resizes; mark overflowing edges
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const upd = () => {
      const max = el.scrollWidth - el.clientWidth;
      setMore({ l: max > 1 && el.scrollLeft > 1, r: max > 1 && el.scrollLeft < max - 1 });
      const sel = el.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (pill && sel && indRef.current) {
        const b = boxOf(sel);
        placeInd(indRef.current, b);
        prevBox.current = b;
      }
    };
    el.addEventListener('scroll', upd, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(upd);
      ro.observe(el);
    }
    const raf = requestAnimationFrame(() => {
      // bring the selected tab into view on narrow screens
      const sel = el.querySelector<HTMLElement>('[aria-selected="true"]');
      if (sel && el.scrollWidth > el.clientWidth + 1) el.scrollLeft = Math.max(0, sel.offsetLeft - (el.clientWidth - sel.offsetWidth) / 2);
      upd();
    });
    return () => {
      el.removeEventListener('scroll', upd);
      ro?.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [pill]);

  return (
    <div
      ref={ref}
      className={cls(pill ? 'pill-tabs' : 'line-tabs', size && `pill-tabs--${size}`, hasInd && 'has-ind', more.l && 'has-more-l', more.r && 'has-more-r', className)}
      role="tablist"
      aria-label={label || undefined}
      onKeyDown={(e) => rovingKeyDown(e, '[role="tab"]', { select: true })}
    >
      {pill ? <span ref={indRef} className="pill-tabs__ind" aria-hidden="true" /> : null}
      {items.map((it) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            className={pill ? 'pill-tabs__tab' : 'line-tabs__tab'}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${it.value}` : undefined}
            aria-selected={on}
            aria-controls={idPrefix && on ? `${idPrefix}-panel-${it.value}` : undefined}
            tabIndex={on ? 0 : -1}
            disabled={it.disabled}
            data-tab={it.value}
            onClick={() => {
              if (it.value !== value) onChange?.(it.value);
            }}
          >
            {it.icon ? <Icon name={it.icon} /> : null}
            <span>{it.label}</span>
            {it.count != null ? <i className="tab-count"> {format(it.count)}</i> : null}
          </button>
        );
      })}
    </div>
  );
}
function useTabNumber() {
  const lang = useLang();
  return { format: (n: number) => Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'ko-KR') };
}

// ───────────────────────── chips (toggle buttons, aria-pressed) ─────────────────────────
export interface ChipItem<V extends string> {
  value: V;
  label: ReactNode;
  count?: number;
  /** tool/series colour dot */
  dot?: string;
  color?: string;
}
export interface ChipsProps<V extends string> {
  items: ReadonlyArray<ChipItem<V>>;
  values: readonly V[];
  onChange?: (values: V[]) => void;
  label?: string;
  /** false: exactly one can be on (a second press keeps it) */
  multi?: boolean;
  /** at most n on (shows the "최대 n개" hint and shakes when exceeded) */
  max?: number;
  /** at least one stays on */
  required?: boolean;
  className?: string;
}
export function Chips<V extends string>({ items, values, onChange, label, multi = true, max, required, className }: ChipsProps<V>) {
  const t = useT();
  const lang = useLang();
  const [warn, setWarn] = useState(false);
  const [shake, setShake] = useState<V | null>(null);
  const toggle = (v: V) => {
    const on = values.includes(v);
    let next: V[];
    if (on) {
      if (!multi) return;
      if (required && values.length === 1) return;
      next = values.filter((x) => x !== v);
    } else if (!multi) next = [v];
    else if (max && values.length >= max) {
      setWarn(true);
      setShake(v);
      setTimeout(() => setShake(null), 420);
      return;
    } else next = [...values, v];
    setWarn(false);
    onChange?.(next);
  };
  const row = (
    <div className={cls('chip-row', className)} role="group" aria-label={label || undefined}>
      {items.map((it) => (
        <button
          key={it.value}
          className={cls('chip', shake === it.value && 'is-no')}
          type="button"
          aria-pressed={values.includes(it.value)}
          style={it.color ? { ['--chip-c' as string]: it.color } : undefined}
          onClick={() => toggle(it.value)}
        >
          <Icon name="check" />
          {it.dot ? <span className="chip__dot" style={{ background: it.dot }} aria-hidden="true" /> : null}
          <span>{it.label}</span>
          {it.count != null ? (
            <>
              {' '}
              <i>{Number(it.count).toLocaleString(lang === 'en' ? 'en-US' : 'ko-KR')}</i>
            </>
          ) : null}
        </button>
      ))}
    </div>
  );
  if (!max) return row;
  return (
    <div className="chips-wrap">
      {row}
      <p className={cls('chip-hint', warn && 'is-warn')} aria-live="polite">
        {t('opt.metricsMax', { n: max })}
      </p>
    </div>
  );
}

// ───────────────────────── switch (role=switch + state text) ─────────────────────────
export function Switch({ checked, onChange, label, desc, disabled, className }: { checked: boolean; onChange?: (on: boolean) => void; label: ReactNode; desc?: ReactNode; disabled?: boolean; className?: string }) {
  const t = useT();
  const id = useId();
  return (
    <div className={cls('switch-row', className)}>
      <span className="switch-row__label">
        <span id={`${id}-l`}>{label}</span>
        {desc ? <small id={`${id}-d`}>{desc}</small> : null}
      </span>
      <span className="switch-text" aria-hidden="true">
        {t(checked ? 'common.on' : 'common.off')}
      </span>
      <button className="switch" type="button" role="switch" aria-checked={checked} aria-labelledby={`${id}-l`} aria-describedby={desc ? `${id}-d` : undefined} disabled={disabled} onClick={() => onChange?.(!checked)} />
    </div>
  );
}

// ───────────────────────── select ─────────────────────────
export interface SelectOption {
  value: string;
  label: string;
  /** Shown greyed out (e.g. a comparison the person may not use — say why in the field hint). */
  disabled?: boolean;
}
export interface SelectProps {
  options: ReadonlyArray<SelectOption | string>;
  value: string;
  onChange?: (v: string) => void;
  label?: ReactNode;
  hideLabel?: boolean;
  size?: 'sm';
  inline?: boolean;
  disabled?: boolean;
  /** Options are people's own text (conversation titles …): marked .rich (excluded from the wording check). */
  human?: boolean;
  className?: string;
}
export function Select({ options, value, onChange, label, hideLabel, size, inline, disabled, human, className }: SelectProps) {
  const id = useId();
  return (
    <div className={cls('field', inline && 'field--inline', className)}>
      {label ? (
        <label className={hideLabel ? 'visually-hidden' : 'field__label'} htmlFor={id}>
          {label}
        </label>
      ) : null}
      <span className="select-wrap">
        <select className={cls('input', 'select', size === 'sm' && 'input--sm', human && 'rich')} id={id} value={value} disabled={disabled} onChange={(e) => onChange?.(e.target.value)}>
          {options.map((op) => {
            const v = typeof op === 'object' ? op.value : op;
            const lb = typeof op === 'object' ? op.label : op;
            return (
              <option key={v} value={v} disabled={typeof op === 'object' ? op.disabled : undefined}>
                {lb}
              </option>
            );
          })}
        </select>
        <Icon name="chevD" className="select-wrap__chev" />
      </span>
    </div>
  );
}

// ───────────────────────── text field ─────────────────────────
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: 'sm';
  /** id of the input (generated when absent) */
  inputId?: string;
  inputRef?: Ref<HTMLInputElement>;
}
/** Visible label, hint and error wired with aria-describedby / aria-invalid (plan 8.7 forms). */
export function Field({ label, hint, error, size, inputId, inputRef, className, ...input }: FieldProps) {
  const auto = useId();
  const id = inputId ?? auto;
  const describedBy = [error ? `${id}-e` : null, hint ? `${id}-h` : null, input['aria-describedby'] ?? null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      {label ? (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <input {...input} ref={inputRef} id={id} className={cls('input', size === 'sm' && 'input--sm', className)} aria-invalid={error ? true : input['aria-invalid']} aria-describedby={describedBy} />
      {hint ? (
        <p className="field__hint" id={`${id}-h`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={`${id}-e`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ───────────────────────── search box (IME aware) ─────────────────────────
export interface SearchBoxProps {
  label: string;
  value?: string;
  placeholder?: string;
  /** Called after typing stops (default 250 ms); never while a Korean syllable is being composed. */
  onSearch: (q: string) => void;
  delay?: number;
  size?: 'sm';
  showLabel?: boolean;
  className?: string;
}
export function SearchBox({ label, value = '', placeholder, onSearch, delay = 250, size, showLabel, className }: SearchBoxProps) {
  const t = useT();
  const id = useId();
  const [text, setText] = useState(value);
  const composing = useRef(false);
  const last = useRef<string | null>(value);
  const cb = useRef(onSearch);
  cb.current = onSearch;
  const fire = useRef(
    debounce((v: string) => {
      if (composing.current || v === last.current) return;
      last.current = v;
      cb.current(v);
    }, delay),
  ).current;
  useEffect(() => () => fire.cancel(), [fire]);
  useEffect(() => {
    setText(value);
    last.current = value;
  }, [value]);
  return (
    <div className={cls('search-box', className)}>
      {showLabel ? (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <span className="search-box__wrap">
        <Icon name="search" className="search-box__ic" />
        <input
          className={cls('input', 'search-box__input', size === 'sm' && 'input--sm')}
          id={id}
          type="search"
          value={text}
          placeholder={placeholder ?? t('common.search')}
          autoComplete="off"
          spellCheck={false}
          aria-label={showLabel ? undefined : label}
          onCompositionStart={() => {
            composing.current = true;
          }}
          onCompositionEnd={(e) => {
            composing.current = false;
            fire(e.currentTarget.value);
          }}
          onChange={(e) => {
            setText(e.target.value);
            if (!(e.nativeEvent as InputEvent).isComposing) fire(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Escape' && text) {
              e.preventDefault();
              e.stopPropagation();
              setText('');
              fire('');
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              last.current = null;
              fire(e.currentTarget.value);
            }
          }}
        />
      </span>
    </div>
  );
}

// ───────────────────────── pages (043) ─────────────────────────
/** Rows per page in tables (activity log and people tables). */
export const PAGE_SIZE = 15;
const PAGER_MS = 350;
export function Pager({ page, pages, onChange, label }: { page: number; pages: number; onChange: (n: number) => void; label?: string }) {
  const lang = useLang();
  const navRef = useRef<HTMLElement>(null);
  const indRef = useRef<HTMLSpanElement>(null);
  const prevPage = useRef<number | null>(null);
  const [hasInd, setHasInd] = useState(false);
  const total = Math.max(1, pages || 1);
  const cur = Math.min(Math.max(1, page || 1), total);
  useLayoutEffect(() => {
    const nav = navRef.current;
    const ind = indRef.current;
    if (!nav || !ind) return;
    const curBtn = nav.querySelector<HTMLElement>('.pager__num[aria-current="page"]');
    if (!curBtn) return;
    const to = boxOf(curBtn);
    placeInd(ind, to);
    setHasInd(true);
    const from = prevPage.current;
    prevPage.current = cur;
    if (from == null || from === cur || reduceMotion() || typeof ind.animate !== 'function') return;
    // start from the nearest number still shown, the leading edge arrives first (55 %), then the tail follows
    let best: HTMLElement | null = null;
    for (const b of Array.from(nav.querySelectorAll<HTMLElement>('.pager__num'))) {
      if (!best || Math.abs(Number(b.dataset.page) - from) < Math.abs(Number(best.dataset.page) - from)) best = b;
    }
    if (!best || best === curBtn) return;
    const fb = boxOf(best);
    const right = to.x > fb.x;
    const mid: Box = right ? { x: fb.x, y: to.y, w: to.x + to.w - fb.x, h: to.h } : { x: to.x, y: to.y, w: fb.x + fb.w - to.x, h: to.h };
    const kf = (b: Box, offset?: number) => ({ width: `${b.w}px`, height: `${b.h}px`, transform: xf(b), ...(offset != null ? { offset } : {}) });
    ind.animate([kf(fb), kf(mid, 0.55), kf(to)], { duration: PAGER_MS, easing: PILL_EASE });
  }, [cur, total]);
  if (total <= 1) return null;
  const nums: Array<number | '…'> = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || Math.abs(i - cur) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  const go = (n: number) => {
    if (n >= 1 && n <= total && n !== cur) onChange(n);
  };
  const en = lang === 'en';
  const prevL = en ? 'Previous page' : '이전 쪽';
  const nextL = en ? 'Next page' : '다음 쪽';
  return (
    <nav ref={navRef} className={cls('pager', hasInd && 'has-ind')} aria-label={label || (en ? 'Pages' : '쪽 이동')}>
      <span ref={indRef} className="pager__ind" aria-hidden="true" />
      <button className="pager__btn" type="button" disabled={cur <= 1} aria-label={prevL} data-tip={prevL} onClick={() => go(cur - 1)}>
        <Icon name="chevL" />
      </button>
      {nums.map((n, i) =>
        n === '…' ? (
          <span key={`gap${i}`} className="pager__gap" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={n}
            className="pager__btn pager__num"
            type="button"
            aria-current={n === cur ? 'page' : undefined}
            aria-label={en ? `Page ${n}` : `${n}쪽`}
            data-page={n}
            onClick={() => go(n)}
          >
            {n}
          </button>
        ),
      )}
      <button className="pager__btn" type="button" disabled={cur >= total} aria-label={nextL} data-tip={nextL} onClick={() => go(cur + 1)}>
        <Icon name="chevR" />
      </button>
    </nav>
  );
}
